import { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { labService, labStepService } from '../../services'
import { useProgressStore } from '../../stores/progressStore'
import achievementToast from '../../components/AchievementToast'
import CelebrationModal from '../../components/CelebrationModal'
import LabStepCheckbox from '../../components/LabStepCheckbox'
import FileUpload from '../../components/capstones/FileUpload'
import ResourceTree from '../../components/ResourceTree'
import {
  BeakerIcon,
  ClockIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  BookOpenIcon,
  LinkIcon,
  ListBulletIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline'
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid'

export default function LabView() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { fetchProgress, getCompletionPercentage } = useProgressStore()
  const [lab, setLab] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [completing, setCompleting] = useState(false)
  const [submissionUrl, setSubmissionUrl] = useState('')
  const [submissionNotes, setSubmissionNotes] = useState('')
  const [showSubmitForm, setShowSubmitForm] = useState(false)
  const [showCelebrationModal, setShowCelebrationModal] = useState(false)
  const [earnedBadges, setEarnedBadges] = useState([])
  const [draftSubmissionId, setDraftSubmissionId] = useState(null)
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [validationErrors, setValidationErrors] = useState({})
  
  // Lab steps state
  const [steps, setSteps] = useState([])
  const [stepStats, setStepStats] = useState(null)
  const [stepsLoading, setStepsLoading] = useState(false)

  useEffect(() => {
    fetchLab()
    fetchLabSteps()
  }, [id])

  const fetchLab = async () => {
    try {
      setLoading(true)
      const data = await labService.getById(id)
      setLab(data)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load lab')
    } finally {
      setLoading(false)
    }
  }

  const fetchLabSteps = async () => {
    try {
      setStepsLoading(true)
      const data = await labStepService.getSteps(id)
      setSteps(data.steps || [])
      setStepStats(data.stats || null)
    } catch (err) {
      console.error('Failed to load lab steps:', err)
    } finally {
      setStepsLoading(false)
    }
  }

  const handleToggleStep = async (stepId) => {
    try {
      const result = await labStepService.toggleStep(id, stepId)
      
      // Update the step in the local state
      setSteps(prevSteps => 
        prevSteps.map(step => 
          step.id === stepId 
            ? { ...step, is_completed: result.is_completed, completed_at: result.completed_at }
            : step
        )
      )

      // Recalculate stats
      const updatedSteps = steps.map(step => 
        step.id === stepId 
          ? { ...step, is_completed: result.is_completed }
          : step
      )
      const completedCount = updatedSteps.filter(s => s.is_completed).length
      const percentage = steps.length > 0 ? Math.round((completedCount / steps.length) * 100) : 0
      
      setStepStats({
        total_steps: steps.length,
        completed_steps: completedCount,
        completion_percentage: percentage
      })

      // Update lab progress percentage based on step completion
      await labService.updateProgress(id, {
        progress_percentage: percentage,
        status: percentage === 100 ? 'completed' : (percentage > 0 ? 'in_progress' : 'not_started')
      })

      // Refresh lab data to show updated progress
      await fetchLab()
      await fetchProgress()

      // Show toast feedback
      if (result.is_completed) {
        achievementToast.success(`Step ${updatedSteps.find(s => s.id === stepId)?.step_number} completed! ✓`)
      }

      // If all steps completed, show celebration
      if (percentage === 100 && result.is_completed) {
        setTimeout(() => {
          achievementToast.success('🎉 All lab steps completed!')
        }, 500)
      }
    } catch (err) {
      achievementToast.error('Failed to update step. Please try again.')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setValidationErrors({}) // Clear previous errors
    
    try {
      setSubmitting(true)
      await labService.submit(id, {
        content: submissionUrl, // Backend expects 'content' not 'submission_url'
        notes: submissionNotes,
      })
      alert('Lab submitted successfully! 🎉')
      setShowSubmitForm(false)
      setSubmissionUrl('')
      setSubmissionNotes('')
      setDraftSubmissionId(null)
      fetchLab() // Refresh to show updated progress
    } catch (err) {
      // Capture validation errors from Laravel
      if (err.response?.data?.errors) {
        setValidationErrors(err.response.data.errors)
      }
      alert(err.response?.data?.message || 'Failed to submit lab')
    } finally {
      setSubmitting(false)
    }
  }

  const handleShowSubmitForm = async () => {
    setShowSubmitForm(true)
    try {
      // Get or create draft submission for file uploads
      const { submission } = await labService.getDraftSubmission(id)
      setDraftSubmissionId(submission.id)
    } catch (err) {
      console.error('Failed to load draft submission:', err)
    }
  }

  const handleMarkComplete = async () => {
    if (completing || lab?.user_progress?.status === 'completed') return
    
    try {
      setCompleting(true)
      const response = await labService.updateProgress(id, {
        status: 'completed',
        progress_percentage: 100,
      })
      
      // Mark all steps as completed
      if (steps.length > 0) {
        const incompleteSteps = steps.filter(step => !step.is_completed)
        for (const step of incompleteSteps) {
          try {
            await labStepService.toggleStep(id, step.id)
          } catch (err) {
            console.error(`Failed to complete step ${step.id}:`, err)
          }
        }
        // Refresh steps to show all as completed
        await fetchLabSteps()
      }
      
      // Show lab completion toast
      achievementToast.labComplete(lab.title)

      // Check for earned badges
      if (response.earned_badges && response.earned_badges.length > 0) {
        setEarnedBadges(response.earned_badges)
        
        // Show individual badge toasts
        response.earned_badges.forEach((badge, index) => {
          setTimeout(() => {
            achievementToast.badge(badge)
          }, (index + 1) * 1500)
        })

        // Show celebration modal
        setTimeout(() => {
          setShowCelebrationModal(true)
        }, response.earned_badges.length * 1500 + 1000)
      }
      
      await fetchLab()
      await fetchProgress()

      // Check for milestone achievements
      const newCompletionPercentage = getCompletionPercentage()
      if ([25, 50, 75, 100].includes(newCompletionPercentage)) {
        setTimeout(() => {
          achievementToast.milestone(newCompletionPercentage)
        }, response.earned_badges?.length ? response.earned_badges.length * 1500 + 2000 : 2000)
      }
    } catch (err) {
      achievementToast.error(err.response?.data?.message || 'Failed to mark lab as complete')
    } finally {
      setCompleting(false)
    }
  }

  const getDifficultyColor = (lab) => {
    const minutes = lab.estimated_time_minutes
    if (minutes <= 90) return 'text-green-600 bg-green-50 border-green-200'
    if (minutes <= 120) return 'text-yellow-600 bg-yellow-50 border-yellow-200'
    return 'text-red-600 bg-red-50 border-red-200'
  }

  const getDifficultyText = (lab) => {
    const minutes = lab.estimated_time_minutes
    if (minutes <= 90) return 'Beginner'
    if (minutes <= 120) return 'Intermediate'
    return 'Advanced'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cte-blue mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading lab...</p>
        </div>
      </div>
    )
  }

  if (error || !lab) {
    return (
      <div className="space-y-4">
        <Link to="/labs" className="inline-flex items-center text-purple-600 hover:text-purple-700">
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Labs
        </Link>
        <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
          <div className="flex items-center">
            <svg className="w-6 h-6 text-red-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="text-red-800 font-semibold">Error loading lab</h3>
              <p className="text-red-700 text-sm mt-1">{error || 'Lab not found'}</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const isCompleted = lab.progress?.status === 'completed'

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Link to="/labs" className="inline-flex items-center text-purple-600 hover:text-purple-700 font-semibold">
        <ArrowLeftIcon className="h-4 w-4 mr-2" />
        Back to Labs
      </Link>

      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-2xl p-8 text-white">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center mb-3">
              <BeakerIcon className="h-8 w-8 mr-3" />
              <span className="text-sm font-semibold bg-purple-800/50 px-3 py-1 rounded-full">
                {lab.code}
              </span>
            </div>
            <h1 className="text-4xl font-bold mb-3">{lab.title}</h1>
            <p className="text-purple-100 text-lg mb-6">{lab.description}</p>
            
            <div className="flex items-center flex-wrap gap-4">
              <span className={`text-sm font-semibold px-3 py-1 rounded-full border bg-white/10 border-white/20 text-white`}>
                {getDifficultyText(lab)}
              </span>
              <div className="flex items-center text-purple-100">
                <ClockIcon className="h-5 w-5 mr-2" />
                {lab.estimated_time_minutes} minutes
              </div>
              {lab.module && (
                <Link 
                  to={`/modules/${lab.module.id}`}
                  className="flex items-center text-purple-100 hover:text-white transition-colors"
                >
                  <BookOpenIcon className="h-5 w-5 mr-2" />
                  {lab.module.title}
                </Link>
              )}
            </div>
          </div>
          {isCompleted && (
            <div className="flex items-center bg-green-500 rounded-full px-4 py-2">
              <CheckCircleSolid className="h-6 w-6 mr-2" />
              <span className="font-semibold">Completed</span>
            </div>
          )}
        </div>
      </div>

      {/* Lab Steps Progress Indicator */}
      {stepStats && steps.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <ChartBarIcon className="h-6 w-6 text-blue-600 mr-2" />
              <h3 className="text-lg font-bold text-gray-900">Lab Progress</h3>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-blue-600">
                {stepStats.completion_percentage}%
              </p>
              <p className="text-sm text-gray-600">
                {stepStats.completed_steps} of {stepStats.total_steps} steps
              </p>
            </div>
          </div>
          <div className="progress-bar h-3">
            <div 
              className="progress-fill bg-blue-600 transition-all duration-500" 
              style={{ width: `${stepStats.completion_percentage}%` }}
            />
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Lab Instructions */}
          <div className="bg-white rounded-xl border border-gray-200 p-8 overflow-hidden">
            <div className="prose prose-lg max-w-none 
              prose-pre:overflow-x-auto prose-pre:whitespace-pre prose-pre:rounded-lg
              prose-code:whitespace-pre-wrap prose-code:break-words
              [&_pre]:overflow-x-auto [&_pre]:max-w-full">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {lab.content}
              </ReactMarkdown>
            </div>
          </div>

          {/* Lab Steps */}
          {steps.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center mb-6">
                <ListBulletIcon className="h-6 w-6 text-purple-600 mr-2" />
                <h3 className="text-xl font-bold text-gray-900">Lab Steps</h3>
              </div>
              
              {stepsLoading ? (
                <div className="text-center py-8 text-gray-500">
                  Loading steps...
                </div>
              ) : (
                <div className="space-y-3">
                  {steps.map((step) => (
                    <LabStepCheckbox
                      key={step.id}
                      step={step}
                      onToggle={handleToggleStep}
                      disabled={isCompleted}
                    />
                  ))}
                </div>
              )}
              
              {stepStats && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">
                      Total estimated time: {steps.reduce((sum, s) => sum + s.estimated_minutes, 0)} minutes
                    </span>
                    <span className="font-semibold text-purple-600">
                      {stepStats.completed_steps}/{stepStats.total_steps} completed
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Submit Lab Section */}
          {!isCompleted && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              {!showSubmitForm ? (
                <div className="text-center py-6">
                  <DocumentTextIcon className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Ready to submit your work?
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Share your GitHub repository or project files for review
                  </p>
                  <div className="flex justify-center gap-3">
                    <button
                      onClick={handleShowSubmitForm}
                      className="bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors flex items-center"
                    >
                      <DocumentTextIcon className="w-5 h-5 mr-2" />
                      Submit Lab
                    </button>
                    <button
                      onClick={handleMarkComplete}
                      disabled={completing || lab?.user_progress?.status === 'completed'}
                      className={`px-6 py-3 rounded-lg font-semibold flex items-center transition-all ${
                        lab?.user_progress?.status === 'completed'
                          ? 'bg-green-100 text-green-700 border-2 border-green-700 cursor-not-allowed'
                          : 'border-2 border-purple-600 text-purple-600 hover:bg-purple-50'
                      } ${completing ? 'opacity-50 cursor-wait' : ''}`}
                    >
                      {completing ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing...
                        </>
                      ) : lab?.user_progress?.status === 'completed' ? (
                        <>
                          <CheckCircleSolid className="w-5 h-5 mr-2" />
                          Completed
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon className="w-5 h-5 mr-2" />
                          Mark as Complete
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Submit Your Lab</h3>
                  
                  {/* Validation Errors Summary */}
                  {Object.keys(validationErrors).length > 0 && (
                    <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-red-800">
                            There were errors with your submission
                          </h3>
                          <div className="mt-2 text-sm text-red-700">
                            <ul className="list-disc list-inside space-y-1">
                              {Object.entries(validationErrors).map(([field, errors]) => (
                                <li key={field}>{errors[0]}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <label htmlFor="submission_url" className="block text-sm font-semibold text-gray-700 mb-2">
                      Repository URL or Project Link *
                    </label>
                    <input
                      id="submission_url"
                      type="text"
                      value={submissionUrl}
                      onChange={(e) => {
                        setSubmissionUrl(e.target.value)
                        if (validationErrors.content || validationErrors.submission_url) {
                          setValidationErrors(prev => ({ ...prev, content: undefined, submission_url: undefined }))
                        }
                      }}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                        (validationErrors.content || validationErrors.submission_url)
                          ? 'border-red-300 bg-red-50' 
                          : 'border-gray-200'
                      }`}
                      placeholder="https://github.com/username/repo"
                    />
                    {(validationErrors.content || validationErrors.submission_url) && (
                      <p className="mt-1 text-sm text-red-600">
                        {validationErrors.content?.[0] || validationErrors.submission_url?.[0]}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="notes" className="block text-sm font-semibold text-gray-700 mb-2">
                      Notes (Optional)
                    </label>
                    <textarea
                      id="notes"
                      value={submissionNotes}
                      onChange={(e) => {
                        setSubmissionNotes(e.target.value)
                        if (validationErrors.notes) {
                          setValidationErrors(prev => ({ ...prev, notes: undefined }))
                        }
                      }}
                      rows={4}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                        validationErrors.notes 
                          ? 'border-red-300 bg-red-50' 
                          : 'border-gray-200'
                      }`}
                      placeholder="Add any notes about your implementation..."
                    />
                    {validationErrors.notes && (
                      <p className="mt-1 text-sm text-red-600">
                        {validationErrors.notes[0]}
                      </p>
                    )}
                  </div>

                  {/* File Upload Section */}
                  {draftSubmissionId && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Supporting Files (Optional)
                      </label>
                      <FileUpload
                        submissionId={draftSubmissionId}
                        onFilesChange={setUploadedFiles}
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        Upload code files, screenshots, or documentation
                      </p>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? 'Submitting...' : 'Submit Lab'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowSubmitForm(false)}
                      className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Prerequisites */}
          {lab.prerequisites && lab.prerequisites.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <CheckCircleIcon className="h-5 w-5 mr-2 text-purple-600" />
                Prerequisites
              </h3>
              <ul className="space-y-2">
                {lab.prerequisites.map((prereq, idx) => (
                  <li key={idx} className="flex items-center text-sm text-gray-600">
                    <span className="w-2 h-2 bg-purple-400 rounded-full mr-3"></span>
                    {prereq}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Resources */}
          {lab.resources && lab.resources.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <LinkIcon className="h-5 w-5 mr-2 text-purple-600" />
                Resources
              </h3>
              <ResourceTree resources={lab.resources} />
            </div>
          )}

          {/* Progress */}
          {lab.progress && (
            <div className="bg-purple-50 rounded-xl border border-purple-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Your Progress</h3>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-700 font-medium">Status</span>
                    <span className="text-purple-600 font-semibold capitalize">
                      {lab.progress.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
                {lab.progress.completion_percentage > 0 && (
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-700 font-medium">Completion</span>
                      <span className="text-purple-600 font-semibold">
                        {Math.round(lab.progress.completion_percentage)}%
                      </span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill bg-purple-600" 
                        style={{ width: `${lab.progress.completion_percentage}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
